import { resetFilters, clearSearch, updateHeaderLogo } from './headerFilter';
import { getActiveCategoryData } from './wheel';
const archiveRecommendations = document.querySelector('section.archive-recommendations');
const cards = fetchRecommendationCards();

/**
 * Main initialization function for recommendations component
 * Sets up back and reset button functionality
 */
export default function () {
    if (!archiveRecommendations) return;

    backButton();
    resetButton();
    setMinHeight();

    window.addEventListener('resize', setMinHeight);
}

/**
 * Sets up the back button functionality to return to the wheel view
 */
function backButton() {
    const backButton = archiveRecommendations.querySelector('.js-archive-recommendations--back');

    if (!backButton) return;

    backButton.addEventListener('click', () => {
        clearSearch();
        upWheel();
        scrollTop();

        const segment = document.querySelector('.wheel__segment.active');
        if (!segment) return;
        const activeCategoryData = getActiveCategoryData(segment);
        updateHeaderLogo(activeCategoryData.logo, activeCategoryData.title);
    });
}

/**
 * Sets up the reset button functionality to clear all filters
 */
function resetButton() {
    const resetButton = archiveRecommendations.querySelector('.js-archive-recommendations--reset');
    if (!resetButton) return;

    resetButton.addEventListener('click', () => {
        resetFilters();
        upWheel();
        scrollTop();

        const segment = document.querySelector('.wheel__segment.active');
        if (!segment) return;
        const activeCategoryData = getActiveCategoryData(segment);
        updateHeaderLogo(activeCategoryData.logo, activeCategoryData.title);
    });
}

/**
 * Gets all recommendation card elements from the archive section
 * @returns {NodeList} List of recommendation card elements
 */
function getRecommendationCards() {
    return archiveRecommendations.querySelectorAll('.card-recommendation');
}

/**
 * Fetches and processes all recommendation cards with their data
 * @returns {Array} Array of objects containing card elements, terms, and titles
 */
function fetchRecommendationCards() {
    const cards = getRecommendationCards();
    return Array.from(cards).map((card) => ({
        element: card,
        terms: JSON.parse(card.dataset.terms || '[]'),
        title: card.dataset.title.toLowerCase(),
        excerpt: card.dataset.excerpt.toLowerCase(),
        idNumber: card.dataset.idNumber,
        idNumberLower: card.dataset.idNumber.toLowerCase(),
    }));
}

/**
 * Shows filtered recommendation cards based on active filters
 * @param {Object} activeData - Object containing current filter data
 * @param {boolean} doDownWheel - Whether to animate the wheel down
 */
export function showRecommendationCards(activeData, doDownWheel = true) {
    cards.forEach((card) => {
        card.element.classList.remove('active');
    });

    let filteredCards = filterCards(activeData);
    filteredCards.forEach((card) => {
        card.element.classList.add('active');
    });

    prepareResultsText(filteredCards, activeData.searchQuery);
    toggleNotFound(filteredCards, activeData.searchQuery);

    if (doDownWheel) {
        downWheel();
    }
}

/**
 * Filters recommendation cards based on active filters
 * @param {Object} activeData - Object containing current filter data
 * @returns {Array} Array of filtered card objects
 */
function filterCards(activeData) {
    let filteredCards = [];

    if (activeData.categoryId) {
        filteredCards = cards.filter((card) => card.terms.categories.includes(activeData.categoryId));
    } else {
        filteredCards = cards;
    }

    if (activeData.subcategories) {
        Object.entries(activeData.subcategories).forEach(([taxonomy, terms]) => {
            filteredCards = filteredCards.filter((card) => card.terms[taxonomy].includes(terms));
        });
    }

    if (activeData.searchQuery) {
        const activeSearchLower = activeData.searchQuery.toLowerCase();
        filteredCards = filteredCards.filter((card) => card.title.includes(activeSearchLower) || card.excerpt.includes(activeSearchLower) || card.idNumberLower.includes(activeSearchLower));
    }

    return filteredCards;
}

let lastWheelRotation = 0;
/**
 * Animates the wheel down by rotating it 90 degrees
 */
function downWheel() {
    if (document.body.classList.contains('down-wheel')) return;

    document.body.classList.add('down-wheel');
    const wheelCircle = document.querySelector('.wheel__circle');
    let wheelRotation = wheelCircle.style.transform;
    if (wheelRotation) {
        lastWheelRotation = parseInt(wheelRotation.replace('rotate(', '').replace('deg)', ''));
    }

    wheelCircle.style.transform = `rotate(${lastWheelRotation + 90}deg)`;

    setTimeout(() => {
        document.body.classList.add('down-wheel-remove');
    }, 1000);
}

/**
 * Returns the wheel to its original position
 */
function upWheel() {
    document.body.classList.remove('down-wheel-remove');
    document.body.classList.remove('down-wheel');
    const wheelCircle = document.querySelector('.wheel__circle');
    wheelCircle.style.transform = `rotate(${lastWheelRotation}deg)`;
}

/**
 * Updates the results text based on filtered cards and search query
 * @param {Array} filteredCards - Array of filtered card objects
 * @param {string} search - Current search query
 */
function prepareResultsText(filteredCards, search = false, isReset = false) {
    const resultsTexts = document.querySelectorAll('.js-archive--results');

    resultsTexts.forEach((text) => {
        let isSearch = false;
        if (search && text.classList.contains('have-search')) {
            isSearch = true;
        }

        const resultsCount = filteredCards.length;
        let textContent = '';

        if (resultsCount === 1) {
            textContent = `${resultsCount} result found`;
            text.classList.remove('hidden');
        } else if (resultsCount > 1) {
            if (!isReset) {
                textContent = `${resultsCount} results found`;
                text.classList.remove('hidden');
            } else {
                textContent = ``;
                text.classList.add('hidden');
            }
        } else {
            textContent = `No results found`;
            text.classList.add('hidden');
        }

        if (isSearch) {
            textContent += ` for`;
            textContent += `<span class="archive-recommendations__results-search">“${search}”</span>`;
        }

        text.innerHTML = textContent;
    });
}

/**
 * Resets all recommendation cards to their initial state
 * Shows all cards and updates the results text
 */
export function resetRecommendationCards() {
    cards.forEach((card) => {
        card.element.classList.remove('active');
    });

    prepareResultsText(cards, false, true);
}

/**
 * Toggles the visibility of the "not found" message
 * @param {Array} filteredCards - Array of filtered card objects
 * @param {boolean} isSearch - Whether the current filter is a search query
 */
function toggleNotFound(filteredCards, isSearch = false) {
    if (filteredCards.length === 0) {
        showNotFound(isSearch);
    } else {
        hideNotFound();
    }
}

/**
 * Shows the "not found" message with appropriate text
 * @param {boolean} isSearch - Whether the current filter is a search query
 */
function showNotFound(isSearch = false) {
    const notFound = archiveRecommendations.querySelector('.js-archive-recommendations--not-found');
    if (!notFound) return;

    if (isSearch) {
        notFound.innerHTML = `Please try a different keyword`;
    } else {
        notFound.innerHTML = `No results found`;
    }

    notFound.classList.remove('hidden');
}

/**
 * Hides the "not found" message
 */
function hideNotFound() {
    const notFound = archiveRecommendations.querySelector('.js-archive-recommendations--not-found');
    if (!notFound) return;

    notFound.classList.add('hidden');
}

/**
 * Scrolls to the top of the page
 */
function scrollTop() {
    window.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
}

/**
 * Calculates the minimum height of the archive recommendations section
 * @returns {string} The minimum height
 */
const calculateMinHeight = () => {
    const headerHeight = document.querySelector('.header').offsetHeight;
    const minHeight = `calc(100dvh - ${headerHeight}px)`;
    return minHeight;
};

const setMinHeight = () => {
    const minHeight = calculateMinHeight();
    document.documentElement.style.setProperty('--archive-recommendations-min-height', minHeight);
};
